'use client';
import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'motion/react';
import {
  Zap,
  Fuel,
  Droplets,
  Factory,
  Pill,
  UtensilsCrossed,
  Anchor,
  Truck,
  Mountain,
  Server,
  Building2,
} from 'lucide-react';

const industries = [
  {
    id: 'energy',
    name: 'Energy & Utilities',
    icon: Zap,
    description: 'Power generation, grid distribution, substations',
    systems: ['SCADA', 'DCS', 'RTU', 'Protection Relays'],
    color: 'amber',
  },
  {
    id: 'oil-gas',
    name: 'Oil & Gas',
    icon: Fuel,
    description: 'Extraction, refining, pipelines, terminals',
    systems: ['SCADA', 'SIS/ESD', 'Metering', 'Gas Detection'],
    color: 'slate',
  },
  {
    id: 'water',
    name: 'Water & Wastewater',
    icon: Droplets,
    description: 'Treatment plants, distribution, pumping stations',
    systems: ['SCADA', 'RTU', 'Telemetry', 'Quality Monitoring'],
    color: 'teal',
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Factory,
    description: 'Production lines, assembly, quality control',
    systems: ['PLC', 'HMI', 'MES', 'Motion Control'],
    color: 'amber',
  },
  {
    id: 'pharma',
    name: 'Pharmaceuticals',
    icon: Pill,
    description: 'Batch production, clean rooms, compliance',
    systems: ['DCS', 'Batch Control', 'LIMS', 'BMS'],
    color: 'slate',
  },
  {
    id: 'food',
    name: 'Food & Beverage',
    icon: UtensilsCrossed,
    description: 'Processing, packaging, cold chain',
    systems: ['PLC', 'Batch Control', 'Packaging Lines', 'Cold Storage'],
    color: 'teal',
  },
  {
    id: 'ports',
    name: 'Ports & Maritime',
    icon: Anchor,
    description: 'Cargo handling, vessel traffic, terminals',
    systems: ['SCADA', 'VTS', 'Crane Control', 'Gate Systems'],
    color: 'amber',
  },
  {
    id: 'transport',
    name: 'Transport & Logistics',
    icon: Truck,
    description: 'Rail, fleet management, logistics hubs',
    systems: ['Signalling', 'Fleet Management', 'Ticketing', 'PIDS'],
    color: 'slate',
  },
  {
    id: 'mining',
    name: 'Mining & Minerals',
    icon: Mountain,
    description: 'Extraction, processing, conveyors',
    systems: ['SCADA', 'Ventilation', 'Conveyor Control', 'Crusher'],
    color: 'teal',
  },
  {
    id: 'data-centre',
    name: 'Data Centres',
    icon: Server,
    description: 'Cooling, power distribution, monitoring',
    systems: ['BMS', 'DCIM', 'UPS Control', 'Fire Suppression'],
    color: 'amber',
  },
];

const colorClasses = {
  amber: {
    bg: 'bg-[hsl(var(--amber))]/10',
    border: 'border-[hsl(var(--amber))]/30',
    text: 'text-[hsl(var(--amber))]',
    iconBg: 'bg-gradient-to-br from-[hsl(var(--amber))] to-[hsl(var(--amber-light))]',
    tag: 'bg-[hsl(var(--amber))]/10 text-[hsl(var(--amber))] border-[hsl(var(--amber))]/20',
  },
  slate: {
    bg: 'bg-[hsl(var(--slate-dark))]/5',
    border: 'border-[hsl(var(--slate-dark))]/20',
    text: 'text-[hsl(var(--slate-dark))]',
    iconBg: 'bg-gradient-to-br from-[hsl(var(--slate-dark))] to-[hsl(var(--slate-medium))]',
    tag: 'bg-[hsl(var(--slate-dark))]/5 text-[hsl(var(--slate-dark))] border-[hsl(var(--slate-dark))]/10',
  },
  teal: {
    bg: 'bg-[hsl(var(--teal))]/10',
    border: 'border-[hsl(var(--teal))]/30',
    text: 'text-[hsl(var(--teal))]',
    iconBg: 'bg-gradient-to-br from-[hsl(var(--teal))] to-[hsl(var(--teal-light))]',
    tag: 'bg-[hsl(var(--teal))]/10 text-[hsl(var(--teal))] border-[hsl(var(--teal))]/20',
  },
};

function IndustryCard({
  industry,
  index,
  isInView,
  isActive,
  onHover,
}: {
  industry: (typeof industries)[0];
  index: number;
  isInView: boolean;
  isActive: boolean;
  onHover: () => void;
}) {
  const Icon = industry.icon;
  const colors = colorClasses[industry.color as keyof typeof colorClasses];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      onMouseEnter={onHover}
      className="group cursor-pointer"
    >
      <div
        className={`relative h-full p-5 rounded-xl border-2 transition-all duration-300 ${isActive
            ? `${colors.bg} ${colors.border} shadow-lg scale-105`
            : 'bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--slate-medium))]/30 hover:shadow-md'
          }`}
      >
        {/* Icon */}
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 ${isActive ? colors.iconBg : 'bg-[hsl(var(--muted))]'
            }`}
        >
          <Icon
            className={`w-5 h-5 ${isActive ? 'text-white' : 'text-[hsl(var(--slate-medium))]'} transition-colors`}
          />
        </div>

        {/* Title */}
        <h4 className="font-serif font-medium text-[hsl(var(--slate-deep))] mb-1">
          {industry.name}
        </h4>

        {/* Description - shows on active */}
        <AnimatePresence>
          {isActive && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-sm text-[hsl(var(--slate-medium))] overflow-hidden"
            >
              {industry.description}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default function Industries() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeIndustry, setActiveIndustry] = useState(industries[0]);

  const colors =
    colorClasses[activeIndustry.color as keyof typeof colorClasses];

  return (
    <section
      ref={ref}
      id="industries"
      className="relative py-24 lg:py-32 bg-[hsl(var(--muted))]/30 overflow-hidden"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 bg-grid-pattern-subtle opacity-50" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--amber))]/10 border border-[hsl(var(--amber))]/20 text-sm font-medium text-[hsl(var(--amber))] mb-6">
            <Building2 className="w-4 h-4" />
            Industry Coverage
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[hsl(var(--slate-deep))] mb-6 text-balance">
            Built for{' '}
            <span className="text-gradient-amber">Critical Infrastructure</span>
          </h2>

          <p className="text-lg text-[hsl(var(--slate-medium))] leading-relaxed">
            Pre-configured templates for 10+ industries with the systems,
            functions, and metrics that matter most.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left: Industries Grid */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {industries.map((industry, index) => (
                <IndustryCard
                  key={industry.id}
                  industry={industry}
                  index={index}
                  isInView={isInView}
                  isActive={activeIndustry.id === industry.id}
                  onHover={() => setActiveIndustry(industry)}
                />
              ))}
            </div>
          </div>

          {/* Right: Active Industry Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2"
          >
            <div className="sticky top-32">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndustry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className={`p-8 rounded-2xl border-2 ${colors.bg} ${colors.border}`}
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div
                      className={`w-14 h-14 rounded-xl ${colors.iconBg} flex items-center justify-center shadow-lg`}
                    >
                      <activeIndustry.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-serif font-medium text-[hsl(var(--slate-deep))]">
                        {activeIndustry.name}
                      </h3>
                      <p className="text-[hsl(var(--slate-medium))]">
                        {activeIndustry.description}
                      </p>
                    </div>
                  </div>

                  {/* Systems */}
                  <div>
                    <div className="text-xs font-medium text-[hsl(var(--slate-medium))] uppercase tracking-wider mb-3">
                      Pre-configured Systems
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeIndustry.systems.map((system, i) => (
                        <motion.span
                          key={system}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.1 }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${colors.tag}`}
                        >
                          {system}
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mt-6 pt-6 border-t border-[hsl(var(--border))] grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-mono font-semibold text-[hsl(var(--slate-deep))]">
                        15+
                      </div>
                      <div className="text-xs text-[hsl(var(--slate-medium))]">
                        System templates
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-mono font-semibold text-[hsl(var(--slate-deep))]">
                        8+
                      </div>
                      <div className="text-xs text-[hsl(var(--slate-medium))]">
                        Function templates
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mt-6 text-center"
              >
                <p className="text-sm text-[hsl(var(--slate-medium))] mb-3">
                  Don&apos;t see your industry?
                </p>
                <button className="text-sm font-medium text-[hsl(var(--amber))] hover:text-[hsl(var(--amber-light))] animated-underline">
                  Contact us for custom templates →
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
